// @ts-nocheck
import {
  cleanHeaderText,
  normalizeHeaderToken,
} from '../settlementSheetConfig.js';
import { normalizeCommissionDrugCode } from '../commissionDrugCode.js';
import {
  hasColumnMergesInLayoutArea,
  fillVerticalMergedColumnValues,
} from '../sheetRowReader.js';

function normalizeCommissionExcelLineBreaks(value) {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const s = String(value)
    .replace(/\r\n|\r|\n|\u2028|\u2029/g, ' ')
    .replace(/[\u00a0\u200b-\u200d\ufeff\u2060]/g, '')
    .replace(/ +/g, ' ')
    .trim();
  return s === '' ? null : s;
}

/** 수수료율 셀 → 0~1 소수(백분율). 숫자 외 문자 제거 후, 1 초과 시 /100 */
function parseCommissionRateValue(value) {
  if (value == null || value === '') {
    return null;
  }

  let parsed;
  if (typeof value === 'number' && Number.isFinite(value)) {
    parsed = value;
  } else {
    const text = normalizeCommissionExcelLineBreaks(value);
    if (text == null) {
      return null;
    }

    const numericText = String(text).replace(/[^\d.]/g, '');
    if (!numericText || numericText === '.') {
      return null;
    }

    const [integerPart = '', ...fractionParts] = numericText.split('.');
    const cleaned =
      fractionParts.length === 0
        ? integerPart
        : `${integerPart}.${fractionParts.join('')}`;

    parsed = Number.parseFloat(cleaned);
    if (!Number.isFinite(parsed)) {
      return null;
    }
  }

  const normalized = parsed > 1 ? parsed / 100 : parsed;
  return Number(normalized.toFixed(4));
}

/** 약가 셀 → 숫자. 쉼표·「원」 등 제거 후 parseFloat */
function parseCommissionPriceValue(value) {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const text = normalizeCommissionExcelLineBreaks(value);
  if (text == null) {
    return null;
  }

  const numericText = String(text).replace(/,/g, '').replace(/원/g, '').trim();
  if (!numericText) {
    return null;
  }

  const parsed = Number.parseFloat(numericText);
  return Number.isFinite(parsed) ? parsed : null;
}

const COMMISSION_SUMMARY_PATTERNS = [
  /총합계/,
  /소계/,
  /^합계$/,
  /정산내역서/,
  /요약/,
  /^계$/,
];

const COMMISSION_FIELD_RULES = [
  {
    key: 'drugCode',
    patterns: [
      /^보험코드/,
      /^보험청구코드/,
      /^제품코드$/,
      /^edi코드$/i,
      /^edi청구코드$/,
      /^청구코드$/,
      /^kd코드$/,
      /^품목코드$/,
      /^약품코드$/,
      /^표준코드$/,
    ],
  },
  {
    key: 'drugName',
    patterns: [
      /^제품명$/,
      /^제품명\s*\(/,
      /^제\s*품\s*명$/,
      /^품목명$/,
      /^품목$/,
      /^품명$/,
      /^품\s*목\s*명$/,
      /^제품$/,
      /^유니메드\s*제품명$/,
      /^처방약호명$/,
      /^자재명$/,
      /^의약품명$/,
    ],
  },
  {
    key: 'price',
    patterns: [
      /^보험약가/,
      /^보험단가$/,
      /^보험가$/,
      /^보험\s*약가/,
      /^약가/,
      /^약\s*가/,
      /^기준가$/,
      /^기준약가$/,
      /^단가$/,
      /^보험수가$/,
      /^포장약가$/,
    ],
  },
  {
    key: 'commissionRate',
    patterns: [
      /^수수료율$/,
      /^수수료$/,
      /^정책$/,
      /^요율/,
      /^요\s*율/,
      /^cso수수료$/,
      /^%$/,
      /^합산%$/,
      /^기본%$/,
      /^최종수수료/,
      /^법인요율표/,
      /^마케팅\s*용역료/,
      /^구간\/요율/,
      /^2026년\s*수수료율$/,
      /^5월\s*수수료율$/,
      /^26년\s*3월\s*edi부터\s*적용\s*수수료율$/i,
    ],
  },
  {
    key: 'commissionNote',
    patterns: [/^비고/, /^비\s*고/, /^공지/, /^기타\s*참고/],
  },
  {
    key: 'incentive',
    patterns: [
      /^인센티브$/,
      /^추가수수료$/,
      /^추가\s*수수료$/,
      /^추가\s*%$/,
      /^특별\s*수수료$/,
    ],
  },
  {
    key: 'pharmaCompanyName',
    patterns: [
      /^제약사$/,
      /^제약사명$/,
      /^허가권자$/,
      /^허가사$/,
      /^제\s*조\s*사$/,
      /^허가제조사$/,
      /^제조사$/,
    ],
  },
];

/** 템플릿 columnMapping 키 → 종합 출력 헤더(COMMISSION_DRIVE_FIXED_HEADERS) */
const COMMISSION_COLUMN_FIELDS = [
  { key: 'drugCode', outputHeader: '제품코드', label: '제품코드' },
  { key: 'drugName', outputHeader: '제품명', label: '제품명' },
  { key: 'price', outputHeader: '보험가', label: '약가' },
  {
    key: 'commissionRate',
    outputHeader: '제약사수수료율',
    label: '수수료율',
  },
  {
    key: 'commissionNote',
    outputHeader: '비고1(제약사 참고사항)',
    label: '수수료 비고',
  },
  { key: 'incentive', outputHeader: '인센티브', label: '인센티브' },
  {
    key: 'incentiveNote',
    outputHeader: '인센티브_메모',
    label: '인센티브 비고',
  },
  { key: 'outOfStock', outputHeader: '품절여부', label: '품절여부' },
  {
    key: 'outOfStockFrom',
    outputHeader: '품절(From)',
    label: '품절 시작일',
  },
  { key: 'outOfStockTo', outputHeader: '품절(To)', label: '품절 종료일' },
  {
    key: 'filteringDisabled',
    outputHeader: '신규금지',
    label: '필터링 제외',
  },
  {
    key: 'yearMonth',
    outputHeader: '년월',
    label: '기준년월',
  },
  {
    key: 'pharmaCompanyName',
    outputHeader: '제약사',
    label: '제약사명(시트)',
  },
];

function normalizeColumnMappingSources(value) {
  if (!value) {
    return [];
  }
  return (Array.isArray(value) ? value : [value])
    .map((item) => cleanText(item))
    .filter(Boolean);
}

function mergeColumnMappingConfig(config = {}) {
  const merged = {
    ...(config.fieldHeaders || {}),
    ...(config.columnMapping || {}),
  };

  if (
    merged.pharmaceuticalCompanyName != null &&
    merged.pharmaCompanyName == null
  ) {
    merged.pharmaCompanyName = merged.pharmaceuticalCompanyName;
  }

  return merged;
}

function inferFieldAliasesFromHeaders(headerLabels = [], explicitMapping = {}) {
  const aliases = {};

  for (const header of headerLabels) {
    if (!header) continue;
    const normalizedCell = normalizeHeader(header);
    for (const { key, patterns } of COMMISSION_FIELD_RULES) {
      if (aliases[key]) continue;
      const matched = patterns.some(
        (pattern) =>
          pattern.test(normalizedCell) || pattern.test(cleanText(header)),
      );
      if (matched) {
        aliases[key] = [header];
        break;
      }
    }
  }

  for (const [key, source] of Object.entries(explicitMapping || {})) {
    const sources = normalizeColumnMappingSources(source);
    if (sources.length) {
      aliases[key] = sources;
    }
  }

  return aliases;
}

function resolveFieldAliases(headerLabels = [], config = {}) {
  const explicit = mergeColumnMappingConfig(config);
  const aliases = {};

  for (const [key, source] of Object.entries(explicit)) {
    const sources = normalizeColumnMappingSources(source);
    if (sources.length) {
      aliases[key] = sources;
    }
  }

  const inferred = inferFieldAliasesFromHeaders(headerLabels, {});
  for (const [key, values] of Object.entries(inferred)) {
    if (!aliases[key]?.length) {
      aliases[key] = values;
    }
  }

  return aliases;
}

function applyColumnIndexOverrides(columnMap = {}, overrides = {}) {
  const next = { ...columnMap };
  const normalizedOverrides = { ...(overrides || {}) };

  if (
    normalizedOverrides.pharmaceuticalCompanyName != null &&
    normalizedOverrides.pharmaCompanyName == null
  ) {
    normalizedOverrides.pharmaCompanyName =
      normalizedOverrides.pharmaceuticalCompanyName;
  }

  for (const [field, index] of Object.entries(normalizedOverrides)) {
    if (Number.isFinite(index)) {
      next[field] = index;
    }
  }
  return next;
}

function getSheetMerges(context, sheetName) {
  const map = context?.sheetMergesMap;
  if (map && sheetName in map) {
    return map[sheetName] || [];
  }
  const sheet = context?.workbook?.Sheets?.[sheetName];
  return sheet?.['!merges'] || [];
}

function resolveMergedFillColumnIndices(layoutConfig = {}, columnMap = {}) {
  if (layoutConfig.fillMergedMappedColumns) {
    return [
      ...new Set(
        Object.values(columnMap).filter((index) => Number.isFinite(index)),
      ),
    ];
  }

  if (Array.isArray(layoutConfig.fillMergedColumnFields)) {
    return [
      ...new Set(
        layoutConfig.fillMergedColumnFields
          .map((field) => columnMap[field])
          .filter((index) => Number.isFinite(index)),
      ),
    ];
  }

  if (layoutConfig.fillMergedDrugNameColumn && columnMap.drugName != null) {
    return [columnMap.drugName];
  }

  return [];
}

function prepareCommissionSheetRows(
  context,
  sheetName,
  rows = [],
  layoutConfig = {},
  columnMap = {},
) {
  const columnsToFill = resolveMergedFillColumnIndices(layoutConfig, columnMap);
  if (!columnsToFill.length) {
    return rows;
  }

  const dataStartRowIndex =
    readDataStartRowIndex(layoutConfig) ??
    (Number.isFinite(layoutConfig.headerRowIndex)
      ? layoutConfig.headerRowIndex + (layoutConfig.headerRowCount ?? 1)
      : 0);
  const merges = getSheetMerges(context, sheetName);
  let preparedRows = rows.map((row) => (Array.isArray(row) ? [...row] : row));

  for (const columnIndex of columnsToFill) {
    preparedRows = fillVerticalMergedColumnValues(
      preparedRows,
      merges,
      columnIndex,
      { dataStartRowIndex },
    );
  }

  return preparedRows;
}

function resolveColumnMapWithOptionalOverrides(
  headerLabels = [],
  layoutConfig = {},
  fieldAliases = {},
  context = null,
  sheetName = '',
) {
  const columnMap = resolveColumnMapFromHeaderRow(
    headerLabels,
    layoutConfig,
    fieldAliases,
  );
  const overrides = layoutConfig.columnIndexOverrides;
  if (!overrides || !Object.keys(overrides).length) {
    return { columnMap, usedColumnIndexOverrides: false };
  }

  const merges = getSheetMerges(context, sheetName);
  const shouldApply = hasColumnMergesInLayoutArea(merges, {
    headerRowIndex: layoutConfig.headerRowIndex,
    headerRowCount: layoutConfig.headerRowCount ?? 1,
    dataStartRowIndex: layoutConfig.dataStartRowIndex,
  });

  if (!shouldApply) {
    return { columnMap, usedColumnIndexOverrides: false };
  }

  return {
    columnMap: applyColumnIndexOverrides(columnMap, overrides),
    usedColumnIndexOverrides: true,
  };
}

function buildResolvedColumnMappings(
  headerLabels = [],
  fieldAliases = {},
  options = {},
) {
  const { definedMapping = {}, columnMap: columnMapOverride } = options;
  const columnMap =
    columnMapOverride ||
    buildColumnMap(headerLabels, buildNormalizedAliases(fieldAliases));
  const definedKeys = new Set(Object.keys(definedMapping || {}));
  const entries = [];

  for (const def of COMMISSION_COLUMN_FIELDS) {
    const sources = fieldAliases[def.key] || [];
    const sourceHeader = sources[0] || '';
    const columnIndex = columnMap[def.key];
    const matched = columnIndex != null;
    const isDefined = definedKeys.has(def.key);

    if (!matched && !isDefined && !sourceHeader) {
      continue;
    }

    entries.push({
      fieldKey: def.key,
      fieldLabel: def.label,
      outputHeader: def.outputHeader,
      sourceHeader,
      sourceHeaders: sources,
      columnIndex: columnIndex ?? null,
      matched,
      defined: isDefined,
    });
  }

  return entries;
}

function sheetToIdSuffix(sheetName) {
  return String(sheetName || 'sheet')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9가-힣-]/gi, '');
}

function normalizeHeader(value) {
  return normalizeHeaderToken(value);
}

function cleanText(value) {
  return cleanHeaderText(value);
}

function buildNormalizedAliases(fieldAliases = {}) {
  return Object.fromEntries(
    Object.entries(fieldAliases).map(([key, aliases]) => [
      key,
      aliases
        .map((alias) => normalizeHeader(alias))
        .sort((left, right) => right.length - left.length),
    ]),
  );
}

function getBestHeaderMatch(normalizedCell, normalizedAliases) {
  let bestMatch = null;

  Object.entries(normalizedAliases).forEach(([field, aliases]) => {
    aliases.forEach((alias) => {
      if (!alias || normalizedCell !== alias) return;
      if (!bestMatch || alias.length > bestMatch.alias.length) {
        bestMatch = { field, alias };
      }
    });
  });

  return bestMatch;
}

function buildColumnMap(row = [], normalizedAliases) {
  const columnMap = {};

  row.forEach((cell, columnIndex) => {
    const normalizedCell = normalizeHeader(cell);
    if (!normalizedCell) return;

    const match = getBestHeaderMatch(normalizedCell, normalizedAliases);
    if (!match) return;

    if (columnMap[match.field] == null) {
      columnMap[match.field] = columnIndex;
    }
  });

  return columnMap;
}

/**
 * 헤더 행을 열 index 그대로 유지한 채 스캔해 필드별 열 index를 계산합니다.
 * 1) columnMapping에 적힌 헤더 글자와 셀 값 정규화 후 정확 매칭
 * 2) 미매핑 필드는 fieldAliases 패턴 매칭
 */
function resolveColumnMapFromHeaderRow(
  headerLabels = [],
  config = {},
  fieldAliases = {},
) {
  const labels = (headerLabels || []).map((cell) => cleanText(cell));
  const explicit = mergeColumnMappingConfig(config);
  const columnMap = {};

  labels.forEach((cell, columnIndex) => {
    const normalizedCell = normalizeHeader(cell);
    if (!normalizedCell) return;

    for (const [fieldKey, source] of Object.entries(explicit)) {
      if (columnMap[fieldKey] != null) continue;
      const sources = normalizeColumnMappingSources(source);
      if (sources.some((item) => normalizeHeader(item) === normalizedCell)) {
        columnMap[fieldKey] = columnIndex;
      }
    }
  });

  const normalizedAliases = buildNormalizedAliases(fieldAliases);
  labels.forEach((cell, columnIndex) => {
    const normalizedCell = normalizeHeader(cell);
    if (!normalizedCell) return;

    const match = getBestHeaderMatch(normalizedCell, normalizedAliases);
    if (!match || columnMap[match.field] != null) return;

    columnMap[match.field] = columnIndex;
  });

  return columnMap;
}

function getCellValue(row, columnMap, key) {
  const columnIndex = columnMap[key];
  return columnIndex == null ? null : row[columnIndex];
}

function readMappedCommissionCell(row, columnMap, keys = []) {
  for (const key of keys) {
    const value = normalizeCommissionExcelLineBreaks(
      getCellValue(row, columnMap, key),
    );
    if (value != null && value !== '') {
      return value;
    }
  }
  return null;
}

function isSummaryLikeText(value) {
  const text = cleanText(value);
  if (!text) return false;
  return COMMISSION_SUMMARY_PATTERNS.some((pattern) => pattern.test(text));
}

function countNonEmptyCommissionCells(row = []) {
  return row.filter((cell) => cleanText(cell) !== '').length;
}

function isCommissionSheetEmptyRow(row = []) {
  return countNonEmptyCommissionCells(row) === 0;
}

function isCommissionNoticeTitleCell(value) {
  const text = cleanText(value);
  if (!text) return false;
  return (
    /^※/.test(text) ||
    COMMISSION_SUMMARY_PATTERNS.some((pattern) => pattern.test(text))
  );
}

function isRepeatedCommissionHeaderRow(row = [], headerLabels = []) {
  const normalizedHeaders = headerLabels
    .map((label) => normalizeHeader(label))
    .filter(Boolean);
  if (normalizedHeaders.length < 3) {
    return false;
  }

  const rowCells = (row || []).map(cleanText).filter(Boolean);
  let matches = 0;
  rowCells.forEach((cell) => {
    if (normalizedHeaders.includes(normalizeHeader(cell))) {
      matches += 1;
    }
  });

  return matches >= Math.min(3, Math.ceil(normalizedHeaders.length * 0.5));
}

/** 시트 중간 ※ 안내 행 (품목 데이터 없음) — skip 대상, break 아님 */
function isCommissionInlineNoticeRow(row = [], columnMap = null) {
  const texts = (row || []).map(cleanText).filter(Boolean);
  if (!texts.length) {
    return false;
  }

  const hasNotice = texts.some((text) => /^※/.test(text));
  if (!hasNotice) {
    return false;
  }

  if (columnMap) {
    const drugName = cleanText(getCellValue(row, columnMap, 'drugName'));
    const codeDigits = String(
      cleanText(getCellValue(row, columnMap, 'drugCode')) || '',
    ).replace(/\D/g, '');
    if (codeDigits.length === 9) {
      return false;
    }
    if (
      drugName &&
      !/^※/.test(drugName) &&
      normalizeHeader(drugName) !== normalizeHeader('제품명')
    ) {
      return false;
    }
  } else {
    const hasCode = texts.some(
      (text) => String(text).replace(/\D/g, '').length === 9,
    );
    if (hasCode) {
      return false;
    }
  }

  return true;
}

/** 시트 하단 안내·반복 헤더·합계 등 실제 데이터 종료 행 */
function isCommissionHardDataEndRow(row = [], options = {}) {
  const { headerLabels = [] } = options;

  if (isCommissionSheetEmptyRow(row)) {
    return false;
  }

  const texts = (row || []).map(cleanText).filter(Boolean);
  if (!texts.length) {
    return false;
  }

  const joinedRow = texts.join(' ');
  if (COMMISSION_SUMMARY_PATTERNS.some((pattern) => pattern.test(joinedRow))) {
    return true;
  }

  if (isRepeatedCommissionHeaderRow(row, headerLabels)) {
    return true;
  }

  return false;
}

/** 테이블 블록 사이 분류 제목 행 (제품명·9자리 보험코드 없음) */
function isCommissionTableSectionDividerRowForMap(row = [], columnMap = {}) {
  if (!columnMap || Object.keys(columnMap).length === 0) {
    return false;
  }

  if (isCommissionSheetEmptyRow(row)) {
    return false;
  }

  const drugName = cleanText(getCellValue(row, columnMap, 'drugName'));
  if (drugName) {
    return false;
  }

  const codeDigits = String(
    cleanText(getCellValue(row, columnMap, 'drugCode')) || '',
  ).replace(/\D/g, '');
  if (codeDigits.length === 9) {
    return false;
  }

  return countNonEmptyCommissionCells(row) > 0;
}

function isCommissionTableSectionDividerRow(
  row = [],
  columnMap = {},
  options = {},
) {
  const { columnBlocks = null } = options;

  if (Array.isArray(columnBlocks) && columnBlocks.length > 0) {
    return columnBlocks.every((blockMap) =>
      isCommissionTableSectionDividerRowForMap(row, blockMap),
    );
  }

  return isCommissionTableSectionDividerRowForMap(row, columnMap);
}

function resolveExtractionColumnBlocks(
  layoutConfig = {},
  primaryColumnMap = {},
) {
  const blocks = layoutConfig.columnBlocks;
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return [primaryColumnMap];
  }

  return blocks.map((blockMap) => ({ ...blockMap }));
}

function isCommissionDataEndRow(row = [], options = {}) {
  return isCommissionHardDataEndRow(row, options);
}

function sliceCommissionDataRows(rows = [], dataStartIndex = 0, options = {}) {
  const {
    headerLabels = [],
    columnMap = null,
    columnBlocks = null,
    shouldSkipDataRow = null,
    repeatedHeaderRowAction = 'break',
  } = options;
  const dataRows = [];
  const dataRowIndexes = [];

  for (let rowIndex = dataStartIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];

    if (isCommissionSheetEmptyRow(row)) {
      continue;
    }

    if (isRepeatedCommissionHeaderRow(row, headerLabels)) {
      if (repeatedHeaderRowAction === 'skip') {
        continue;
      }
      break;
    }

    if (isCommissionInlineNoticeRow(row, columnMap)) {
      continue;
    }

    if (isCommissionHardDataEndRow(row, { headerLabels })) {
      break;
    }

    if (
      typeof shouldSkipDataRow === 'function' &&
      shouldSkipDataRow(row, columnMap)
    ) {
      dataRows.push(row);
      dataRowIndexes.push(rowIndex);
      continue;
    }

    if (
      columnMap &&
      isCommissionTableSectionDividerRow(row, columnMap, { columnBlocks })
    ) {
      continue;
    }

    dataRows.push(row);
    dataRowIndexes.push(rowIndex);
  }

  return {
    dataRows,
    dataRowIndexes,
    dataEndIndex: dataRowIndexes.length
      ? dataRowIndexes[dataRowIndexes.length - 1] + 1
      : dataStartIndex,
  };
}

function shouldSkipCommissionRow(record, rawRow = []) {
  const drugName = cleanText(record?.drugName);
  if (!drugName) {
    return true;
  }

  if (isSummaryLikeText(drugName)) {
    return true;
  }

  const joinedRow = rawRow.map(cleanText).filter(Boolean).join(' ');
  if (
    joinedRow &&
    COMMISSION_SUMMARY_PATTERNS.some((pattern) => pattern.test(joinedRow))
  ) {
    return true;
  }

  return false;
}

/** KD코드/약가 2행 구조에서 약가만 있는 보조 행 (국제약품 등) */
function isCommissionMergedPriceSubRow(row = [], columnMap = {}) {
  const drugName = cleanText(getCellValue(row, columnMap, 'drugName'));
  const rawCode = getCellValue(row, columnMap, 'drugCode');
  const codeText = cleanText(rawCode);
  const codeDigits = String(codeText || '').replace(/\D/g, '');

  if (codeText && /원/.test(codeText) && codeDigits.length !== 9) {
    return true;
  }

  if (!drugName && codeDigits.length !== 9) {
    return true;
  }

  return false;
}

/** 국제약품 등 서브행 KD코드 열의 「188원」 형태 → price */
function parseCommissionMergedPriceSubRowValue(row = [], columnMap = {}) {
  const raw = getCellValue(row, columnMap, 'drugCode');
  return parseCommissionPriceValue(raw);
}

/**
 * @returns {import('@/utils/commissionUploadValidation').COMMISSION_UPLOAD_OUTPUT_HEADERS extends readonly (infer U)[] ? Record<string, unknown> : object}
 */
function buildCommissionRecord({
  row,
  columnMap,
  fileName,
  sheetName,
  rowIndex,
  templateId = 'default',
  pharmaceuticalCompanyName = '',
  referenceYearMonth = '',
}) {
  const rawDrugCode = getCellValue(row, columnMap, 'drugCode');
  const drugCode = normalizeCommissionDrugCode(rawDrugCode);

  const cell = (key) => {
    const value = getCellValue(row, columnMap, key);
    return normalizeCommissionExcelLineBreaks(value);
  };

  const rowPharmaCompanyName = readMappedCommissionCell(row, columnMap, [
    'pharmaCompanyName',
    'pharmaceuticalCompanyName',
  ]);

  return {
    id: `${fileName}-${sheetName}-${rowIndex}`,
    rowIndex,
    templateId,
    sourceFileName: fileName,
    sourceSheetName: sheetName,
    pharmaceuticalCompanyName:
      rowPharmaCompanyName || pharmaceuticalCompanyName || '',
    referenceYearMonth: referenceYearMonth || cell('yearMonth') || '',
    drugCode,
    drugName: cell('drugName'),
    price: parseCommissionPriceValue(getCellValue(row, columnMap, 'price')),
    standardCommissionRate: parseCommissionRateValue(cell('commissionRate')),
    standardCommissionNote: cell('commissionNote'),
    incentive: cell('incentive'),
    incentiveNote: cell('incentiveNote'),
    outOfStock: cell('outOfStock'),
    outOfStockFrom: cell('outOfStockFrom'),
    outOfStockTo: cell('outOfStockTo'),
    filteringDisabled: cell('filteringDisabled'),
    rowPharmaCompanyName,
  };
}

function commissionRecordToUploadRow(
  record,
  pharmaCompanyId,
  referenceYearMonth,
) {
  return [
    referenceYearMonth || record.referenceYearMonth || '',
    record.drugCode || '',
    record.drugName || '',
    pharmaCompanyId || '',
    record.price ?? null,
    record.standardCommissionRate ?? null,
    record.standardCommissionNote ?? null,
    record.incentive ?? null,
    record.incentiveNote ?? null,
    record.outOfStock ?? null,
    record.outOfStockFrom ?? null,
    record.outOfStockTo ?? null,
    record.filteringDisabled ?? null,
  ];
}

function sortCommissionRows(rows = []) {
  return [...rows].sort((left, right) => {
    return (
      String(left.pharmaceuticalCompanyName || '').localeCompare(
        String(right.pharmaceuticalCompanyName || ''),
        'ko',
      ) ||
      String(left.drugCode || '').localeCompare(String(right.drugCode || '')) ||
      String(left.drugName || '').localeCompare(
        String(right.drugName || ''),
        'ko',
      )
    );
  });
}

function buildCompositeHeaders(
  rows = [],
  startRowIndex = 0,
  headerRowCount = 2,
) {
  const headerRows = rows.slice(startRowIndex, startRowIndex + headerRowCount);
  const maxColumnLength = headerRows.reduce(
    (max, row) => Math.max(max, Array.isArray(row) ? row.length : 0),
    0,
  );

  return Array.from({ length: maxColumnLength }, (_, columnIndex) => {
    const tokens = headerRows
      .map((row) => cleanText(row?.[columnIndex]))
      .filter(Boolean)
      .filter(
        (token, tokenIndex, array) => array.indexOf(token) === tokenIndex,
      );

    return tokens.join(' ');
  });
}

function countHeaderMatches(headerLabels = [], requiredHeaders = []) {
  return requiredHeaders.filter((header) =>
    headerLabels.some(
      (candidate) => normalizeHeader(candidate) === normalizeHeader(header),
    ),
  ).length;
}

const HEADER_MATCH_MIN_RATIO = 0.75;

function headersMatchRequired(
  headerLabels = [],
  requiredHeaders = [],
  options = {},
) {
  const minRatio = options.minRatio ?? 1;
  if (!requiredHeaders.length) {
    return false;
  }
  const minCount = Math.max(3, Math.ceil(requiredHeaders.length * minRatio));
  return countHeaderMatches(headerLabels, requiredHeaders) >= minCount;
}

function readDataStartRowIndex(config = {}) {
  if (
    config.dataStartRowIndex != null &&
    Number.isFinite(config.dataStartRowIndex)
  ) {
    return config.dataStartRowIndex;
  }
  if (config.dataStartRow != null && Number.isFinite(config.dataStartRow)) {
    return config.dataStartRow;
  }
  return null;
}

function resolveHeaderLayoutOnTargetSheet(rows = [], config = {}) {
  const { requiredHeaders = [], headerRowIndex, headerRowCount = 1 } = config;
  const dataStartRowIndex = readDataStartRowIndex(config);

  const minCount = Math.max(
    3,
    Math.ceil(requiredHeaders.length * HEADER_MATCH_MIN_RATIO),
  );
  const candidates = [];

  const tryCandidate = (rowIndex, rowCount, preferred = false) => {
    if (rowIndex < 0 || rowIndex >= rows.length) return;
    if (rowIndex + rowCount > rows.length) return;

    const headerLabels = resolveHeaderLabels(rows, rowIndex, rowCount);
    const matchedCount = countHeaderMatches(headerLabels, requiredHeaders);
    if (matchedCount < minCount) return;

    candidates.push({
      headerLabels,
      headerRowIndex: rowIndex,
      headerRowCount: rowCount,
      matchedCount,
      preferred,
    });
  };

  if (Number.isFinite(headerRowIndex)) {
    tryCandidate(headerRowIndex, headerRowCount, true);
    if (!candidates.length) {
      return null;
    }

    const fixed = candidates[0];
    const dataStartIndex =
      dataStartRowIndex != null
        ? dataStartRowIndex
        : fixed.headerRowIndex + fixed.headerRowCount;

    return {
      headerLabels: fixed.headerLabels,
      headerRowIndex: fixed.headerRowIndex,
      headerRowCount: fixed.headerRowCount,
      dataStartIndex,
      matchedCount: fixed.matchedCount,
    };
  }

  for (let rowIndex = 0; rowIndex < Math.min(80, rows.length); rowIndex++) {
    for (const rowCount of [1, 2]) {
      tryCandidate(rowIndex, rowCount, false);
    }
  }

  if (!candidates.length) {
    return null;
  }

  candidates.sort((left, right) => {
    if (right.matchedCount !== left.matchedCount) {
      return right.matchedCount - left.matchedCount;
    }
    if (left.preferred !== right.preferred) {
      return left.preferred ? -1 : 1;
    }
    if (left.headerRowIndex !== right.headerRowIndex) {
      return left.headerRowIndex - right.headerRowIndex;
    }
    return left.headerRowCount - right.headerRowCount;
  });

  const best = candidates[0];
  const usesPreferredRow =
    best.preferred && best.matchedCount >= requiredHeaders.length;
  const dataStartIndex =
    usesPreferredRow && dataStartRowIndex != null
      ? dataStartRowIndex
      : best.headerRowIndex + best.headerRowCount;

  return {
    headerLabels: best.headerLabels,
    headerRowIndex: best.headerRowIndex,
    headerRowCount: best.headerRowCount,
    dataStartIndex,
    matchedCount: best.matchedCount,
  };
}

function resolveHeaderLabels(rows, headerRowIndex, headerRowCount = 1) {
  const count = Math.max(1, headerRowCount || 1);
  if (count >= 2) {
    return buildCompositeHeaders(rows, headerRowIndex, count);
  }
  return (rows[headerRowIndex] || []).map(cleanText);
}

function buildCommissionHeaderAreaRows(
  rows = [],
  headerRowIndex = 0,
  headerRowCount = 1,
) {
  const count = Math.max(1, headerRowCount || 1);
  return rows
    .slice(headerRowIndex, headerRowIndex + count)
    .map((cells, offset) => ({
      rowNumber: headerRowIndex + offset + 1,
      cells: Array.isArray(cells) ? cells : [],
    }));
}

function findSheetName(context, targetSheetName, options = {}) {
  const { strict = false } = options;
  const names =
    context.workbook?.SheetNames || Object.keys(context.sheetRowsMap || {});
  if (!names.length) {
    return strict ? null : targetSheetName;
  }

  const target = normalizeHeader(targetSheetName);
  const exact = names.find((name) => normalizeHeader(name) === target);
  if (exact) {
    return exact;
  }

  const partial = names.find((name) => {
    const normalizedName = normalizeHeader(name);
    return normalizedName.includes(target) || target.includes(normalizedName);
  });
  if (partial) {
    return partial;
  }
  return strict ? null : names[0];
}

function getSheetRows(context, sheetName) {
  const map = context.sheetRowsMap || {};
  if (!sheetName) {
    return [];
  }
  if (Array.isArray(map[sheetName])) {
    return map[sheetName];
  }

  const target = normalizeHeader(sheetName);
  const matchedKey = Object.keys(map).find(
    (name) => normalizeHeader(name) === target,
  );
  return matchedKey ? map[matchedKey] : [];
}

function createSheetProfileMatcher(targetSheetName, requiredHeaders) {
  return function matchSheetProfile(profile) {
    return (
      normalizeHeader(profile.sheetName) === normalizeHeader(targetSheetName) &&
      headersMatchRequired(profile.headers, requiredHeaders)
    );
  };
}

function matchFixedHeaderLayout(context, config) {
  const sheetName = findSheetName(context, config.targetSheetName, {
    strict: true,
  });
  if (!sheetName) {
    return false;
  }
  const rows = getSheetRows(context, sheetName);
  return Boolean(resolveHeaderLayoutOnTargetSheet(rows, config));
}

function resolveCommissionSheetLayout(
  context,
  config,
  hasFixedHeaderLayout,
  matchSheetProfile,
) {
  const {
    targetSheetName,
    requiredHeaders,
    headerRowIndex,
    headerRowCount = 1,
  } = config;

  if (hasFixedHeaderLayout) {
    const sheetNameResolved = findSheetName(context, targetSheetName);
    const rows = getSheetRows(context, sheetNameResolved);
    const layout = resolveHeaderLayoutOnTargetSheet(rows, config);
    if (!layout) {
      return null;
    }

    return {
      sheetNameResolved,
      headerLabels: layout.headerLabels,
      headerRowIndex: layout.headerRowIndex,
      headerRowCount: layout.headerRowCount,
      dataStartIndex: layout.dataStartIndex,
    };
  }

  const targetProfile = context.headerProfiles.find(matchSheetProfile);
  if (!targetProfile) {
    return null;
  }

  const sheetNameResolved = targetProfile.sheetName;
  const rows = getSheetRows(context, sheetNameResolved);
  const profileHeaderRowCount = targetProfile.headerRowCount || 1;
  const headerLabels = resolveHeaderLabels(
    rows,
    targetProfile.headerRowIndex,
    profileHeaderRowCount,
  );

  return {
    sheetNameResolved,
    headerLabels,
    headerRowIndex: targetProfile.headerRowIndex,
    headerRowCount: profileHeaderRowCount,
    dataStartIndex:
      targetProfile.dataStartRowIndex ??
      targetProfile.headerRowIndex + profileHeaderRowCount,
  };
}

function buildEffectiveLayoutConfig(baseConfig = {}, overrides = {}) {
  const effective = {
    ...baseConfig,
    ...overrides,
    columnMapping: {
      ...(baseConfig.columnMapping || {}),
      ...(overrides.columnMapping || {}),
    },
    fieldHeaders: {
      ...(baseConfig.fieldHeaders || {}),
      ...(overrides.fieldHeaders || {}),
    },
    columnIndexOverrides: {
      ...(baseConfig.columnIndexOverrides || {}),
      ...(overrides.columnIndexOverrides || {}),
    },
  };

  if (overrides.fillMergedColumnFields != null) {
    effective.fillMergedColumnFields = overrides.fillMergedColumnFields;
  }
  if (overrides.columnBlocks != null) {
    effective.columnBlocks = overrides.columnBlocks;
  }

  effective.dataStartRowIndex = readDataStartRowIndex(effective);
  return effective;
}

function resolveLayoutWithOverrides(
  context,
  baseConfig,
  hasFixedHeaderLayout,
  matchSheetProfile,
  overrides = {},
) {
  const effectiveConfig = buildEffectiveLayoutConfig(baseConfig, overrides);
  const useExplicit =
    overrides.targetSheetName != null ||
    Number.isFinite(overrides.headerRowIndex) ||
    Number.isFinite(overrides.headerRowCount) ||
    Number.isFinite(overrides.dataStartRowIndex);

  if (useExplicit) {
    const sheetName = findSheetName(
      context,
      overrides.targetSheetName ?? effectiveConfig.targetSheetName,
      { strict: false },
    );
    if (!sheetName) {
      return null;
    }

    const headerRowIndex = Number.isFinite(overrides.headerRowIndex)
      ? overrides.headerRowIndex
      : effectiveConfig.headerRowIndex;
    const headerRowCount =
      overrides.headerRowCount ?? effectiveConfig.headerRowCount ?? 1;

    if (!Number.isFinite(headerRowIndex)) {
      return resolveCommissionSheetLayout(
        context,
        effectiveConfig,
        hasFixedHeaderLayout,
        matchSheetProfile,
      );
    }

    const rows = getSheetRows(context, sheetName);
    const headerLabels = resolveHeaderLabels(
      rows,
      headerRowIndex,
      headerRowCount,
    );
    const dataStartIndex =
      readDataStartRowIndex({
        ...effectiveConfig,
        headerRowIndex,
        headerRowCount,
      }) ?? headerRowIndex + headerRowCount;

    return {
      sheetNameResolved: sheetName,
      headerLabels,
      headerRowIndex,
      headerRowCount,
      dataStartIndex,
    };
  }

  return resolveCommissionSheetLayout(
    context,
    effectiveConfig,
    hasFixedHeaderLayout,
    matchSheetProfile,
  );
}

function createCommissionTemplate(config) {
  const {
    id,
    pharmaceuticalCompanyName,
    targetSheetName,
    requiredHeaders,
    fieldHeaders,
    columnMapping,
    headerRowIndex,
    headerRowCount = 1,
  } = config;

  const layoutConfig = {
    ...config,
    dataStartRowIndex: readDataStartRowIndex(config),
  };

  const resolvedColumnMapping = mergeColumnMappingConfig(layoutConfig);

  const hasFixedHeaderLayout =
    headerRowIndex != null && Number.isFinite(headerRowIndex);

  const resolvedId = id || pharmaceuticalCompanyName;
  const resolvedName = pharmaceuticalCompanyName;
  const matchSheetProfile = createSheetProfileMatcher(
    targetSheetName,
    requiredHeaders,
  );

  function buildExtractionPlan(
    headerLabels = [],
    context = null,
    sheetName = '',
    config = layoutConfig,
  ) {
    const normalizedHeaderLabels = (headerLabels || []).map((cell) =>
      cleanText(cell),
    );
    const resolvedColumnMapping = mergeColumnMappingConfig(config);
    const fieldAliases = resolveFieldAliases(
      normalizedHeaderLabels.filter(Boolean),
      config,
    );
    const { columnMap, usedColumnIndexOverrides } =
      resolveColumnMapWithOptionalOverrides(
        normalizedHeaderLabels,
        config,
        fieldAliases,
        context,
        sheetName,
      );
    const columnMappings = buildResolvedColumnMappings(
      normalizedHeaderLabels,
      fieldAliases,
      { definedMapping: resolvedColumnMapping, columnMap },
    );

    return {
      fieldAliases,
      columnMap,
      columnMappings,
      usedColumnIndexOverrides,
      resolvedColumnMapping,
    };
  }

  const layoutDefaults = {
    targetSheetName,
    headerRowIndex,
    headerRowCount,
    dataStartRowIndex: readDataStartRowIndex(layoutConfig),
    columnMapping: { ...resolvedColumnMapping },
    requiredHeaders,
    repeatedHeaderRowAction: config.repeatedHeaderRowAction ?? 'break',
    fillMergedMappedColumns: config.fillMergedMappedColumns ?? false,
    fillMergedDrugNameColumn: config.fillMergedDrugNameColumn ?? false,
    fillMergedColumnFields: config.fillMergedColumnFields ?? null,
    columnIndexOverrides: config.columnIndexOverrides ?? null,
    columnBlocks: config.columnBlocks ?? null,
    mergePriceFromFollowingSubRow:
      config.mergePriceFromFollowingSubRow ?? false,
    hasShouldSkipDataRow: typeof config.shouldSkipDataRow === 'function',
  };

  return {
    id: resolvedId,
    name: resolvedName,
    pharmaceuticalCompanyName,
    columnMapping: resolvedColumnMapping,
    requiredHeaders,
    layoutDefaults,
    match(context) {
      if (!hasFixedHeaderLayout) {
        return false;
      }
      return matchFixedHeaderLayout(context, layoutConfig);
    },
    extract(context, options = {}) {
      const overrides = options.layoutOverrides || {};
      const effectiveConfig = buildEffectiveLayoutConfig(
        layoutConfig,
        overrides,
      );
      const layout = resolveLayoutWithOverrides(
        context,
        effectiveConfig,
        hasFixedHeaderLayout,
        matchSheetProfile,
        overrides,
      );
      if (!layout) {
        return {
          rows: [],
          pharmaceuticalCompanyName,
          templateName: resolvedName,
          columnMappings: [],
        };
      }

      const { sheetNameResolved, headerLabels, dataStartIndex } = layout;

      const { columnMap, columnMappings } = buildExtractionPlan(
        headerLabels,
        context,
        sheetNameResolved,
        effectiveConfig,
      );
      const extractionColumnBlocks = resolveExtractionColumnBlocks(
        effectiveConfig,
        columnMap,
      );
      const extractedRows = [];
      const referenceYearMonth = options.referenceYearMonth || '';
      const sourceRows = getSheetRows(context, sheetNameResolved);
      const rows = prepareCommissionSheetRows(
        context,
        sheetNameResolved,
        sourceRows,
        effectiveConfig,
        columnMap,
      );
      const { dataRows: sheetDataRows, dataRowIndexes } =
        sliceCommissionDataRows(rows, dataStartIndex, {
          headerLabels: headerLabels.filter(Boolean),
          columnMap,
          columnBlocks: effectiveConfig.columnBlocks,
          shouldSkipDataRow: effectiveConfig.shouldSkipDataRow,
          repeatedHeaderRowAction: effectiveConfig.repeatedHeaderRowAction,
        });

      for (
        let rowOffset = 0;
        rowOffset < sheetDataRows.length;
        rowOffset += 1
      ) {
        const row = sheetDataRows[rowOffset];
        if (!Array.isArray(row)) {
          continue;
        }

        const mainSheetRowIndex =
          dataRowIndexes[rowOffset] ?? dataStartIndex + rowOffset;
        let mergePriceSubRowConsumed = false;

        for (
          let blockIndex = 0;
          blockIndex < extractionColumnBlocks.length;
          blockIndex += 1
        ) {
          const blockColumnMap = extractionColumnBlocks[blockIndex];

          if (
            typeof effectiveConfig.shouldSkipDataRow === 'function' &&
            effectiveConfig.shouldSkipDataRow(row, blockColumnMap)
          ) {
            continue;
          }

          const record = buildCommissionRecord({
            row,
            columnMap: blockColumnMap,
            fileName: context.file.name,
            sheetName: sheetNameResolved,
            rowIndex: mainSheetRowIndex,
            templateId: resolvedId,
            pharmaceuticalCompanyName,
            referenceYearMonth,
          });

          if (extractionColumnBlocks.length > 1) {
            record.id = `${record.id}-block${blockIndex}`;
          }

          if (
            effectiveConfig.mergePriceFromFollowingSubRow &&
            blockIndex === 0 &&
            !mergePriceSubRowConsumed
          ) {
            const nextRow = sheetDataRows[rowOffset + 1];
            if (
              nextRow &&
              isCommissionMergedPriceSubRow(nextRow, blockColumnMap)
            ) {
              const mergedPrice = parseCommissionMergedPriceSubRowValue(
                nextRow,
                blockColumnMap,
              );
              if (mergedPrice != null) {
                record.price = mergedPrice;
                record.priceSubRowIndex =
                  dataRowIndexes[rowOffset + 1] ?? mainSheetRowIndex + 1;
              }
              mergePriceSubRowConsumed = true;
            }
          }

          if (shouldSkipCommissionRow(record, row)) {
            continue;
          }

          extractedRows.push(record);
        }

        if (mergePriceSubRowConsumed) {
          rowOffset += 1;
        }
      }

      return {
        rows: extractedRows,
        pharmaceuticalCompanyName,
        templateName: resolvedName,
        columnMappings,
      };
    },
    preview(context, options = {}) {
      const overrides = options.layoutOverrides || {};
      const effectiveConfig = buildEffectiveLayoutConfig(
        layoutConfig,
        overrides,
      );
      const layout = resolveLayoutWithOverrides(
        context,
        effectiveConfig,
        hasFixedHeaderLayout,
        matchSheetProfile,
        overrides,
      );
      if (!layout) {
        return null;
      }

      const {
        sheetNameResolved,
        headerLabels,
        headerRowIndex: resolvedHeaderRowIndex,
        headerRowCount: resolvedHeaderRowCount,
        dataStartIndex,
      } = layout;
      const sourceRows = getSheetRows(context, sheetNameResolved);
      const { columnMappings, columnMap } = buildExtractionPlan(
        headerLabels,
        context,
        sheetNameResolved,
        effectiveConfig,
      );
      const rows = prepareCommissionSheetRows(
        context,
        sheetNameResolved,
        sourceRows,
        effectiveConfig,
        columnMap,
      );
      const resolvedHeaders = headerLabels.map(
        (label, index) => cleanText(label) || `열 ${index + 1}`,
      );
      const { dataRows } = sliceCommissionDataRows(rows, dataStartIndex, {
        headerLabels: resolvedHeaders,
        columnMap,
        columnBlocks: effectiveConfig.columnBlocks,
        shouldSkipDataRow: effectiveConfig.shouldSkipDataRow,
        repeatedHeaderRowAction: effectiveConfig.repeatedHeaderRowAction,
      });
      const headerAreaRows = buildCommissionHeaderAreaRows(
        rows,
        resolvedHeaderRowIndex,
        resolvedHeaderRowCount,
      );

      return {
        sheetName: sheetNameResolved,
        headerRowIndex: resolvedHeaderRowIndex,
        headerRowCount: resolvedHeaderRowCount,
        dataStartRowIndex: dataStartIndex,
        headers: resolvedHeaders,
        headerAreaRows,
        dataRows,
        totalDataRowCount: dataRows.length,
        columnMappings,
        columnMapping: mergeColumnMappingConfig(effectiveConfig),
      };
    },
  };
}

const PREVIEW_EDITABLE_COLUMN_FIELDS = COMMISSION_COLUMN_FIELDS.filter(
  (field) =>
    [
      'drugCode',
      'drugName',
      'price',
      'commissionRate',
      'commissionNote',
    ].includes(field.key),
);

const COMMISSION_REPEATED_HEADER_ACTIONS = [
  { value: 'break', label: '중단 (기본)' },
  { value: 'skip', label: '건너뛰고 계속' },
];

export {
  COMMISSION_COLUMN_FIELDS,
  PREVIEW_EDITABLE_COLUMN_FIELDS,
  COMMISSION_REPEATED_HEADER_ACTIONS,
  normalizeHeader,
  cleanText,
  buildNormalizedAliases,
  buildColumnMap,
  resolveColumnMapFromHeaderRow,
  getCellValue,
  shouldSkipCommissionRow,
  isCommissionMergedPriceSubRow,
  parseCommissionMergedPriceSubRowValue,
  parseCommissionPriceValue,
  parseCommissionRateValue,
  applyColumnIndexOverrides,
  resolveColumnMapWithOptionalOverrides,
  getSheetMerges,
  buildCommissionRecord,
  commissionRecordToUploadRow,
  sortCommissionRows,
  createCommissionTemplate,
  buildEffectiveLayoutConfig,
  inferFieldAliasesFromHeaders,
  resolveFieldAliases,
  buildResolvedColumnMappings,
  mergeColumnMappingConfig,
  sheetToIdSuffix,
  buildCompositeHeaders,
  resolveHeaderLabels,
  buildCommissionHeaderAreaRows,
  findSheetName,
  getSheetRows,
  sliceCommissionDataRows,
};
